// src/hooks/useAutoLogout.js
import { useEffect, useRef } from "react";

/**
 * Auto-logs the user out after `timeoutMs` of the page being "unattended" —
 * either because they stopped interacting with it, or because they switched
 * to another tab / minimized the window / moved to a different app.
 *
 * How it detects "switched to another tab or went elsewhere":
 * Activity listeners (mousemove, keydown, click, scroll, touchstart) only
 * fire on a document that is visible and focused. The moment this tab is
 * hidden or loses focus, those events simply stop firing here — so
 * `lastActivityRef` naturally stops updating, and the elapsed-time check
 * below counts that whole away period as inactivity. No separate "tab
 * switch" flag is needed; it falls out of the same mechanism as ordinary
 * idle detection.
 *
 * Uses wall-clock timestamps (Date.now()) rather than counting timer ticks,
 * so it stays correct even if the browser throttles setInterval while the
 * tab is backgrounded (Chrome, for example, can slow background timers down
 * to roughly once a minute) — whenever the check does run, it still
 * computes the real elapsed time correctly.
 *
 * @param {Function} onTimeout - called once the user has been away/idle for timeoutMs
 * @param {number}   timeoutMs - inactivity threshold in milliseconds (default: 60s = 60000)
 */
export default function useAutoLogout(onTimeout, timeoutMs = 60000000) {
  const lastActivityRef = useRef(Date.now());
  const onTimeoutRef = useRef(onTimeout);
  const firedRef = useRef(false);

  // Always call the latest onTimeout without having to re-attach listeners
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    const markActive = () => {
      lastActivityRef.current = Date.now();
      firedRef.current = false;
    };

    const checkIdle = () => {
      if (firedRef.current) return; // don't fire logout more than once
      if (Date.now() - lastActivityRef.current >= timeoutMs) {
        firedRef.current = true;
        onTimeoutRef.current();
      }
    };

    // Any of these count as "the user is actually here"
    const activityEvents = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    activityEvents.forEach((evt) => window.addEventListener(evt, markActive, { passive: true }));

    // Polling catches plain inactivity — tab stays focused, user just walks away
    const intervalId = setInterval(checkIdle, 5000);

    // Re-check immediately when the tab regains visibility/focus, so coming
    // back after being away past the threshold logs out right away instead
    // of waiting for the next poll tick.
    const handleVisibility = () => {
      if (document.visibilityState === "visible") checkIdle();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", checkIdle);

    return () => {
      activityEvents.forEach((evt) => window.removeEventListener(evt, markActive));
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", checkIdle);
      clearInterval(intervalId);
    };
  }, [timeoutMs]);
}